import { useState, useEffect } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, ArrowLeft } from "lucide-react";

export default function ResetPassword() {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token") || "";

  useEffect(() => {
    if (!token) {
      toast.error("Recovery token not found in URL.");
      navigate("/login");
    }
  }, [token, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: newPassword });
      setCompleted(true);
      toast.success("Password configured successfully!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to reset password.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-24">
      <div className="rtrl-card carbon-bg p-8 border border-rtrl-red/30 shadow-[0_0_40px_rgba(229,25,25,0.05)] relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />
        <div className="scanline" />

        <div className="relative z-10 space-y-6">
          <div className="text-center space-y-2">
            <ShieldAlert className="w-12 h-12 text-rtrl-red mx-auto animate-pulse" />
            <div className="small-label text-rtrl-red">Terminal Recovery</div>
            <h1 className="font-display uppercase text-4xl text-white">Reset Password</h1>
          </div>

          {!completed ? (
            <form onSubmit={submit} className="space-y-6">
              <div>
                <label className="small-label flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> New Password</label>
                <input
                  data-testid="reset-new-password"
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="rtrl-input text-sm placeholder-white/10"
                />
              </div>

              <div>
                <label className="small-label flex items-center gap-1"><KeyRound className="w-3.5 h-3.5" /> Confirm Password</label>
                <input
                  data-testid="reset-confirm-password"
                  type="password"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="rtrl-input text-sm placeholder-white/10"
                />
              </div>

              <button
                data-testid="reset-submit"
                disabled={busy}
                className="rtrl-btn w-full justify-center"
              >
                {busy ? "Saving New Credentials…" : "Update Password"}
              </button>
            </form>
          ) : (
            <div className="text-center space-y-4 py-4">
              <p className="text-emerald-400 font-bold text-sm">Terminal Security Key Configured successfully.</p>
              <p className="text-white/60 text-xs">You can now proceed to log in with your updated password terminal key.</p>
              <Link to="/login" className="rtrl-btn w-full justify-center">Go to Log In</Link>
            </div>
          )}

          {!completed && (
            <div className="pt-4 border-t border-surface-line/40 text-center">
              <Link to="/login" className="text-xs font-mono-tech text-rtrl-silver hover:text-white transition-colors flex items-center justify-center gap-1.5" data-testid="reset-back-to-login">
                <ArrowLeft className="w-4 h-4" /> Cancel and Back to Log In
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}